import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Loader2, CheckCircle2, ClipboardList, ShieldAlert, Heart } from "lucide-react";
import axios from "axios";
import { useReviewQueue } from "../../lib/adminQueues";
import { useSelection } from "../../lib/useSelection";
import { formatDollars } from "../../lib/draftCampaign";
import { SelectCheckbox } from "./SelectCheckbox";
import { BulkActionBar } from "./BulkActionBar";
import { BulkConfirmDialog } from "./BulkConfirmDialog";
import { ApprovalEmailPreviewDialog } from "./ApprovalEmailPreviewDialog";
import { ReviewRow } from "./ReviewRow";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "mra_jwt";
const rowClient = axios.create({ baseURL: API, withCredentials: true });
rowClient.interceptors.request.use((cfg) => {
  const t = typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null;
  if (t) { cfg.headers = cfg.headers || {}; cfg.headers.Authorization = `Bearer ${t}`; }
  return cfg;
});

export const CampaignReviewTab = () => {
  const { items, loading, error, refresh, bulkApprove, bulkApproveEdits, bulkFlag } = useReviewQueue();
  const sel = useSelection();
  const [action, setAction] = useState(null);
  const [rowReject, setRowReject] = useState(null); // { id, title } | null
  const [preview, setPreview] = useState(null); // { id, title } | null
  // action: null | "approve" | "flag-compassionate" | "flag-academic"

  const ids = useMemo(() => items.map((c) => c.id), [items]);
  const editIdsAll = useMemo(() => items.filter((c) => c.kind === "edit").map((c) => c.id), [items]);
  const newIdsAll = useMemo(() => items.filter((c) => c.kind !== "edit").map((c) => c.id), [items]);
  const selectedItems = items.filter((c) => sel.isSelected(c.id));
  const kindOf = (id) => items.find((c) => c.id === id)?.kind || "new";
  const selectedNew = sel.ids.filter((id) => kindOf(id) === "new");
  const selectedEdits = sel.ids.filter((id) => kindOf(id) === "edit");

  const runApprove = async () => {
    const newIds = selectedNew;
    const editIds = selectedEdits;
    const edits = editIds.length ? await bulkApproveEdits(editIds) : { processed: 0, skipped: 0 };
    const res = newIds.length ? await bulkApprove(newIds) : { processed: 0, skipped: 0 };
    const p = res.processed || 0;
    const s = (res.skipped || 0) + (edits.skipped || 0);
    const published = edits.processed || 0;
    if (p) toast.success(`${p} campaign${p === 1 ? "" : "s"} approved — enrollment verified, students notified.`);
    if (published) toast.success(`${published} set${published === 1 ? "" : "s"} of edits published — students notified.`);
    if (s) toast.warning(`${s} item${s === 1 ? "" : "s"} skipped`, {
      description: res.skipped_reason || edits.skipped_reason || "Some items weren't eligible any more.",
    });
    sel.clear();
    setAction(null);
  };

  const runFlag = async (kind) => {
    const res = await bulkFlag(sel.ids, kind);
    const p = res.processed || 0;
    toast.success(`${p} campaign${p === 1 ? "" : "s"} flagged for ${kind} review.`);
    sel.clear();
    setAction(null);
  };

  // Per-row approve: new submissions verify enrollment + publish; edits publish pending changes.
  const singleApprove = async (id) => {
    if (kindOf(id) === "edit") {
      try {
        await rowClient.post(`/admin/campaigns/${id}/changes/approve`);
        toast.success("Edits published — student notified.");
      } catch (e) {
        toast.warning("Could not publish edits.", { description: e?.response?.data?.detail });
      }
      await refresh();
      return;
    }
    const res = await bulkApprove([id]);
    if (res.processed === 1) {
      toast.success("Campaign approved — enrollment verified, page live, student notified.");
    } else {
      toast.warning("Could not approve.", {
        description: res.skipped_reason || "This campaign isn't eligible right now.",
      });
    }
  };

  // Per-row return with reason
  const singleReject = async (reason) => {
    if (!rowReject) return;
    const path = rowReject.kind === "edit" ? `/admin/campaigns/${rowReject.id}/changes/reject` : `/admin/campaigns/${rowReject.id}/reject`;
    await rowClient.post(path, { reason });
    toast.success("Sent back with a note.");
    setRowReject(null);
    await refresh();
  };

  return (
    <div data-testid="tab-campaign-review">
      {error && (
        <p className="mb-6 rounded-md bg-scarlet/8 px-3 py-2 text-sm text-scarlet">{error}</p>
      )}

      {/* Header row w/ select-all */}
      {items.length > 0 && (
        <div className="mb-4 flex items-center justify-between rounded-lg border border-border bg-bone px-5 py-3">
          <label className="flex items-center gap-3 text-sm font-medium text-ink">
            <SelectCheckbox
              testid="review-select-all"
              checked={sel.allSelected(ids)}
              indeterminate={sel.count > 0 && !sel.allSelected(ids)}
              onChange={(v) => (v ? sel.selectAll(ids) : sel.clear())}
              label="Select all campaigns"
            />
            {sel.count === 0
              ? `Select all (${items.length})`
              : `${sel.count} of ${items.length} selected`}
          </label>
          <button
            type="button"
            onClick={refresh}
            data-testid="review-refresh"
            className="text-xs font-semibold uppercase tracking-[0.18em] text-olive hover:text-ink"
          >
            Refresh
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-olive">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading campaigns…
        </div>
      ) : items.length === 0 ? (
        <EmptyBlock
          icon={ClipboardList}
          title="Nothing to review right now."
          body="New submissions and edits to live pages appear here the moment students hit Submit."
          testid="review-empty"
        />
      ) : (
        <div className="space-y-4">
          <AnimatePresence>
            {items.map((c) => (
              <ReviewRow
                key={c.id}
                c={c}
                checked={sel.isSelected(c.id)}
                onToggle={() => sel.toggle(c.id)}
                onSingleApprove={() => singleApprove(c.id)}
                onSingleReject={() => setRowReject({ id: c.id, title: c.title, kind: c.kind })}
                onPreviewEmail={() => setPreview({ id: c.id, title: c.title })}
              />
            ))}
          </AnimatePresence>
        </div>
      )}

      <BulkActionBar count={sel.count} label="campaign" onClear={sel.clear}>
        <button
          type="button"
          onClick={() => setAction("approve")}
          data-testid="review-bulk-approve"
          className="inline-flex items-center gap-1.5 rounded-full bg-yellow px-4 py-2 text-xs font-semibold text-ink transition-colors hover:brightness-95"
        >
          <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2} />
          Approve
        </button>
        <button
          type="button"
          onClick={() => setAction("flag-compassionate")}
          data-testid="review-bulk-flag-compassionate"
          className="inline-flex items-center gap-1.5 rounded-full border border-sand/25 bg-sand/5 px-4 py-2 text-xs font-semibold text-sand transition-colors hover:bg-sand/10"
        >
          <Heart className="h-3.5 w-3.5" strokeWidth={2} />
          Flag: Compassionate
        </button>
        <button
          type="button"
          onClick={() => setAction("flag-academic")}
          data-testid="review-bulk-flag-academic"
          className="inline-flex items-center gap-1.5 rounded-full border border-sand/25 bg-sand/5 px-4 py-2 text-xs font-semibold text-sand transition-colors hover:bg-sand/10"
        >
          <ShieldAlert className="h-3.5 w-3.5" strokeWidth={2} />
          Flag: Academic
        </button>
      </BulkActionBar>

      {/* Confirm dialogs */}
      <BulkConfirmDialog
        open={action === "approve"}
        onOpenChange={(v) => !v && setAction(null)}
        title={<>Approve {sel.count} campaign{sel.count === 1 ? "" : "s"}?</>}
        description="One decision does both jobs: it records the student's enrollment as verified (for the audit trail) and publishes the page. Edits to already-live pages are published in the same pass."
        itemsSummary={<SummaryList items={selectedItems.map((c) => ({
          left: c.title || "(untitled)",
          right: `${c.student_name || c.student_email} · ${formatDollars(c.goal_cents)}`,
        }))} />}
        confirmLabel="Approve — go live"
        onConfirm={runApprove}
      />
      <BulkConfirmDialog
        open={action === "flag-compassionate"}
        onOpenChange={(v) => !v && setAction(null)}
        title={<>Flag {sel.count} for compassionate review?</>}
        description="Marks each campaign for a second read by our care team — status stays pending_review."
        itemsSummary={<SummaryList items={selectedItems.map((c) => ({
          left: c.title || "(untitled)",
          right: c.student_name || c.student_email,
        }))} />}
        confirmLabel="Flag for compassionate review"
        onConfirm={() => runFlag("compassionate")}
      />
      <BulkConfirmDialog
        open={action === "flag-academic"}
        onOpenChange={(v) => !v && setAction(null)}
        title={<>Flag {sel.count} for academic review?</>}
        description="Sends each campaign to our academic-verification specialist. Status stays pending_review."
        itemsSummary={<SummaryList items={selectedItems.map((c) => ({
          left: c.title || "(untitled)",
          right: c.student_school || c.student_email,
        }))} />}
        confirmLabel="Flag for academic review"
        onConfirm={() => runFlag("academic")}
      />

      {/* Per-row reject dialog */}
      <BulkConfirmDialog
        open={!!rowReject}
        onOpenChange={(v) => !v && setRowReject(null)}
        title={<>Return &ldquo;{rowReject?.title || "campaign"}&rdquo; with a note?</>}
        description={rowReject?.kind === "edit" ? "Their live page stays exactly as it is. The note lands on their dashboard next to the returned edits." : "The student receives your note on their dashboard so they know exactly what to fix. Keep it warm and specific."}
        requireNote
        notePlaceholder="e.g. Please add an invoice for the specific costs. The story is great — just needs concrete numbers."
        noteMinLength={8}
        confirmLabel="Send back with note"
        confirmTone="danger"
        onConfirm={singleReject}
      />

      {/* Read-only approval email preview */}
      <ApprovalEmailPreviewDialog
        open={!!preview}
        onOpenChange={(v) => !v && setPreview(null)}
        campaignId={preview?.id}
        title={preview?.title}
      />
    </div>
  );
};

const EmptyBlock = ({ icon: Icon, title, body, testid }) => (
  <div
    data-testid={testid}
    className="rounded-2xl border-2 border-dashed border-border bg-bone p-10 text-center"
  >
    <Icon className="mx-auto h-9 w-9 text-olive" strokeWidth={1.5} />
    <h3 className="mt-3 font-serif text-2xl text-ink">{title}</h3>
    <p className="mt-1 text-sm text-olive">{body}</p>
  </div>
);

export const SummaryList = ({ items }) => (
  <ul className="space-y-1.5">
    {items.slice(0, 8).map((it, i) => (
      <li key={i} className="flex items-baseline justify-between gap-4">
        <span className="truncate font-serif text-base text-ink">{it.left}</span>
        <span className="shrink-0 text-xs text-olive">{it.right}</span>
      </li>
    ))}
    {items.length > 8 && (
      <li className="pt-1 text-xs italic text-olive">…and {items.length - 8} more</li>
    )}
  </ul>
);
